package com.dignicate.web.ui.components.header

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.Canvas
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.ImageData
import kotlin.math.*

/**
 * Header background wavy pattern canvas.
 *
 * Contract:
 * - `enabled` controls whether the animation runs (true) or stays static (false).
 * - `intensity` must be in [0..1], controls visual strength of blobs.
 * - `speed` must be in [0..1], controls animation speed.
 * - `heightPx` should be > 0.
 *
 * This function enforces preconditions with require(...) so callers must satisfy the contract.
 */
@Composable
fun HeaderWavyPatternCanvas(
    isDark: Boolean,
    enabled: Boolean = true,
    intensity: Float = 1.0f, // 0..1
    speed: Float = 1.0f, // 0..1
    heightPx: Int = 88
) {
    // Enforce contract early and loudly
    require(intensity in 0f..1f) { "HeaderWavyPatternCanvas: intensity must be in 0..1" }
    require(speed in 0f..1f) { "HeaderWavyPatternCanvas: speed must be in 0..1" }
    require(heightPx > 0) { "HeaderWavyPatternCanvas: heightPx must be > 0" }

    // Fixed internal buffer for performance (reduced to favor higher fps)
    val bufferW = 320
    val bufferH = 64

    // reduce canvas opacity a bit to soften contrast
    val baseOpacity = if (isDark) 0.85 else 0.6
    // Increase base speed so animation is noticeably faster by default (user requested)
    val baseSppedPerSec = 0.36 // increased base temporal speed to make animation faster

    // Compute scaled parameters from external inputs
    fun lerp(a: Double, b: Double, t: Double) = a + (b - a) * t
    val intensityD = intensity.coerceIn(0f, 1f).toDouble()
    val speedD = speed.coerceIn(0f, 1f).toDouble()
    // warp parameter unused in current flow-field approach (keep BASE_WARP for future use)
    // val warp = BASE_WARP * lerp(0.3, 1.0, intensityD)
    val opacity = baseOpacity * lerp(0.2, 1.0, intensityD)
    val speedPerSec = baseSppedPerSec * lerp(0.2, 2.0, speedD)

    // Compose a Canvas element as a child of the header; style it as an absolute background
    Canvas(attrs = {
        id("header-wavy-canvas")
        // soften blend and filter to reduce harshness
        attr("style", "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:0;opacity:${opacity};mix-blend-mode:normal;filter:contrast(130%) saturate(110%);")
        attr("width", bufferW.toString())
        attr("height", bufferH.toString())
    }) {
        // empty draw scope; we'll access DOM canvas from DisposableEffect
    }

    DisposableEffect(isDark, enabled, intensity, speed, heightPx) {
        // Setup
        val canvas = document.getElementById("header-wavy-canvas") as? HTMLCanvasElement

        if (canvas == null) {
            // nothing to do; provide empty cleanup
            onDispose { }
        } else {
            // Ensure CSS sizing: canvas element has drawing buffer size = bufferW x bufferH
            canvas.width = bufferW
            canvas.height = bufferH
            canvas.style.width = "100%"
            canvas.style.height = "${heightPx}px"

            val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D
            if (ctx == null) {
                onDispose { }
            } else {
                // ImageData buffer
                val imageData: ImageData = ctx.createImageData(bufferW.toDouble(), bufferH.toDouble())
                val data = imageData.data

                // create an offscreen canvas to allow ctx.filter (blur) when drawing the ImageData
                val offscreen = document.createElement("canvas") as HTMLCanvasElement
                offscreen.width = bufferW
                offscreen.height = bufferH
                val offCtx = offscreen.getContext("2d") as? CanvasRenderingContext2D
                // if offCtx is null we'll fall back to direct putImageData
                //
                // Noise helpers
                fun fract(x: Double) = x - floor(x)
                fun hash(ix: Int, iy: Int): Double {
                    val s = sin(ix * 127.1 + iy * 311.7)
                    return fract(s * 43758.5453123)
                }
                fun fade(t: Double) = t * t * (3 - 2 * t)
                fun lerpD(a: Double, b: Double, t: Double) = a + (b - a) * t

                fun valueNoise(x: Double, y: Double): Double {
                    val xi = floor(x).toInt()
                    val yi = floor(y).toInt()
                    val xf = x - xi
                    val yf = y - yi
                    val tl = hash(xi, yi)
                    val tr = hash(xi + 1, yi)
                    val bl = hash(xi, yi + 1)
                    val br = hash(xi + 1, yi + 1)
                    val u = fade(xf)
                    val v = fade(yf)
                    val a = lerpD(tl, tr, u)
                    val b = lerpD(bl, br, u)
                    return lerpD(a, b, v)
                }

                fun fbm(x: Double, y: Double, octaves: Int = 3): Double {
                    var sum = 0.0
                    var amp = 0.5
                    var freq = 1.0
                    repeat(octaves) {
                        sum += amp * valueNoise(x * freq, y * freq)
                        freq *= 2.0
                        amp *= 0.5
                    }
                    return sum
                }

                // Drawing loop
                var rafId: Int? = null
                val reduced = try { window.matchMedia("(prefers-reduced-motion: reduce)").matches } catch (_: Throwable) { false }

                // time integration variables: use rAF timestamp, clamp dt to avoid jumps
                val lastFrameTs = 0.0
                var timeAcc = 0.0
                val timeScale = 1.15 // increased to speed up animation while dt clamping preserves stability

                // Time throttling (keep CPU reasonable)
                val targetFps = 60.0
                val frameInterval = 1000.0 / targetFps

                @Suppress("UNUSED_PARAMETER")
                fun drawFrame(ts: Double) {
                    if (reduced) return
                    // compute dt from rAF timestamp and clamp to avoid large jumps (max ~33ms => ~30fps)
                    val rawDt = if (lastFrameTs == 0.0) frameInterval else (ts - lastFrameTs)
                    // allow larger clamp (50ms) but still prevent massive jumps; targetFps=60 keeps smooth updates
                    val dtMs = rawDt.coerceIn(0.0, 50.0)

                    // advance accumulated time with a conservative TIME_SCALE to avoid too-fast motion
                    timeAcc += dtMs * 0.001 * speedPerSec * timeScale
                    val timeSec = timeAcc

                    // sampling scale used for mapping px/py to noise coords
                    val scale = 0.032

                    var idx = 0
                    for (y in 0 until bufferH) {
                        for (x in 0 until bufferW) {
                            // --- Flow-field based warping (two-stage) with time-perturbed coordinates ---
                            val px = x.toDouble()
                            val py = y.toDouble()
                            // timeSec is provided from accumulated timeAcc above
                            // increase time scales to make the field evolve faster and more chaotically
                            val t1 = timeSec * 2.8
                            val t2 = timeSec * 1.9

                            val flowFreq1 = 0.030
                            val flowFreq2 = 0.055
                            val warp1 = 22.0
                            val warp2 = 10.0

                            fun flowAngle(cx: Double, cy: Double, toff: Double): Double {
                                val v = fbm(cx + toff, cy + toff, 3)
                                return v * (2.0 * PI)
                            }

                            // 1st flow field: use time only as flow parameter; avoid large time offsets in coords
                            val a1 = flowAngle(
                                px * flowFreq1,
                                py * flowFreq1,
                                t1
                            )
                             var wx = cos(a1) * warp1
                             var wy = sin(a1) * warp1

                            // 2nd flow field: stronger time dependence and different phase/offset so fields are decorrelated
                            val a2 = flowAngle(
                                (px + wx) * flowFreq2,
                                (py + wy) * flowFreq2,
                                t2 + 100.0
                            )
                             wx += cos(a2) * warp2
                             wy += sin(a2) * warp2

                            // final sampling coords (apply scale then add small, fast time-based jitter to break translation)
                             val sampleX = (px + wx) * scale
                             val sampleY = (py + wy) * scale
                            // add small jitter that evolves with time and position (keep small to avoid translation)
                            val jitterX = sin(timeSec * 0.8 + px * 0.002) * 3.0
                            val jitterY = cos(timeSec * 0.9 + py * 0.002) * 3.0
                             val nx = sampleX + jitterX
                             val ny = sampleY + jitterY

                            // sample low/high frequency components at warped coords
                            // add strong time-based local perturbations so the shape changes rapidly
                            val timeMix = timeSec * 0.9
                            val jitterScaleLow = 0.12
                            val jitterScaleHigh = 0.06
                            val nxLow = nx * 0.8 + sin(timeMix * 0.9 + nx * 1.3) * jitterScaleLow
                            val nyLow = ny * 0.8 + cos(timeMix * 1.1 + ny * 1.1) * jitterScaleLow
                            val nxHigh = nx * 1.7 + cos(timeMix * 1.3 + ny * 1.7) * jitterScaleHigh
                            val nyHigh = ny * 1.6 + sin(timeMix * 1.5 + nx * 1.5) * jitterScaleHigh
                            // include small time offsets in fbm sampling so the pattern reconfigures smoothly
                            val nLow = fbm(nxLow + sin(timeMix * 0.4) * 0.02, nyLow + cos(timeMix * 0.35) * 0.02, 3)
                            val nHigh = fbm(nxHigh + cos(timeMix * 0.45) * 0.015, nyHigh + sin(timeMix * 0.5) * 0.015, 4)

                            // composite raw noise: big flow + fine detail
                            fun clamp01(x: Double) = x.coerceIn(0.0, 1.0)
                            val combinedRaw = clamp01(nLow * 0.7 + nHigh * 0.3)

                            // contrast curve parameters (as requested)
                            val gain = 1.8
                            val gamma = 0.85
                            fun powSafe(x: Double, p: Double): Double = if (x <= 0.0) 0.0 else exp(ln(x) * p)

                            // apply curve: v2 = clamp((v - 0.5) * gain + 0.5, 0,1); v3 = v2^gamma
                            val v2 = clamp01((combinedRaw - 0.5) * gain + 0.5)
                            val v3 = powSafe(v2.coerceIn(0.0, 1.0), gamma)

                            // make alpha from v3 with a floor so it never becomes too faint
                            var alphaF = ((v3 - 0.15) / 0.85).coerceIn(0.0, 1.0)
                            alphaF *= alphaF * 0.85

                            // color luminance mapping (grayscale but tone varies with v3)
                            val lum = if (isDark) {
                                // white smoke on black: 180..255
                                (180.0 + (255.0 - 180.0) * v3)
                            } else {
                                // black smoke on white: 20..0
                                (20.0 - (20.0 * v3)).coerceIn(0.0, 255.0)
                            }
                            val alphaByte = (alphaF * 255.0).toInt().coerceIn(0, 255)
                            val gray = lum.toInt().coerceIn(0, 255)
                            (data.asDynamic())[idx++] = gray
                            (data.asDynamic())[idx++] = gray
                            (data.asDynamic())[idx++] = gray
                            (data.asDynamic())[idx++] = alphaByte
                        }
                    }

                    // draw via offscreen to allow blur filter
                    if (offCtx != null) {
                        offCtx.putImageData(imageData, 0.0, 0.0)
                        // clear main canvas and draw blurred image
                        ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                        ctx.filter = "blur(6px)"
                        ctx.imageSmoothingEnabled = true
                        ctx.drawImage(offscreen, 0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                        ctx.filter = "none"
                    } else {
                        ctx.imageSmoothingEnabled = true
                        ctx.putImageData(imageData, 0.0, 0.0)
                    }

                    // schedule next frame only if enabled
                    rafId = if (enabled) window.requestAnimationFrame(::drawFrame) else null
                 }

                 // start: if enabled -> animate, else draw one frame
                 if (!reduced) {
                     if (enabled) {
                         rafId = window.requestAnimationFrame(::drawFrame)
                     } else {
                         drawFrame(0.0)
                     }
                 } else {
                     drawFrame(0.0)
                 }

                 onDispose {
                     rafId?.let { id -> try { window.cancelAnimationFrame(id) } catch (_: Throwable) {} }
                      // do NOT remove the canvas DOM node; Compose owns it
                  }
              }
          }
         // DisposableEffect end
     }
 }
